import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { WorkflowService } from "../../service/workflow.service";

@Component({
  selector: "app-s09-workflow-run",
  templateUrl: "./s09-workflow-run.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
})
export class S09WorkflowRunComponent implements OnInit, OnDestroy {
  @Input() wfDefId = 0;
  @Input() brandKey: number | null = null;
  @Input() workflowStatus = "DRAFT";
  @Input() initiatedBy = "";
  @Output() back = new EventEmitter<void>();

  loading = false;
  running = false;
  refreshing = false;
  notice = "";
  codes: string[] = [];
  patients: any[] = [];
  active: any[] = [];
  selectedId: number | null = null;
  run: any = null;
  frmDays: number | null = null;
  private poll: any;

  constructor(private workflowService: WorkflowService) {}

  ngOnInit(): void {
    this.loadPatients();
    this.loadActive();
  }

  ngOnDestroy(): void {
    clearInterval(this.poll);
  }

  get liveAllowed(): boolean {
    return (this.workflowStatus || "DRAFT") === "ACTIVE";
  }

  get waitingFrm(): boolean {
    return this.run?.status === "WAITING" && this.run?.phase === "waiting_frm";
  }

  get waitingDelay(): boolean {
    return this.run?.status === "WAITING" && this.run?.phase === "waiting_delay";
  }

  get waitingApproval(): boolean {
    return this.run?.status === "WAITING" && this.run?.phase === "waiting_approval";
  }

  get selectedActive(): any | null {
    if (!this.selectedId) return null;
    return this.active.find((item) => Number(item.du_patient_id) === Number(this.selectedId)) || null;
  }

  placeFor(patientId: number): string {
    const hit = this.active.find((item) => Number(item.du_patient_id) === Number(patientId));
    return hit?.place || "";
  }

  stepLabel(kind: string): string {
    if (kind === "frmDecision") return "FRM Action";
    if (kind === "wait") return "Wait / Delay";
    if (kind === "createCase") return "Create Case";
    if (kind === "assignCase") return "Assign Case";
    if (kind === "attempt") return "Attempt";
    return kind || "—";
  }

  loadPatients(): void {
    if (!this.wfDefId) return;
    this.loading = true;
    this.workflowService.runCandidates(this.wfDefId, Number(this.brandKey) || 0).subscribe((res) => {
      this.loading = false;
      this.codes = res?.codes || [];
      this.patients = res?.items || [];
      this.notice = res?.message || "";
    });
  }

  start(mode: "TEST" | "LIVE"): void {
    if (!this.selectedId || this.running) return;
    if (this.selectedActive) {
      this.openActive(this.selectedActive);
      this.notice = "This workflow is already running for this patient.";
      return;
    }
    if (mode === "LIVE" && !this.liveAllowed) return;
    this.running = true;
    this.notice = "";
    this.workflowService
      .startRun({
        wf_def_id: this.wfDefId,
        brand_key: Number(this.brandKey) || 0,
        du_patient_id: this.selectedId,
        mode,
        initiated_by: this.initiatedBy,
      })
      .subscribe((res) => {
        this.running = false;
        if (!res) return;
        this.applyRun(res);
        this.loadActive();
      });
  }

  selectPatient(patientId: number): void {
    this.selectedId = patientId;
    const hit = this.active.find((item) => Number(item.du_patient_id) === Number(patientId));
    if (!hit) {
      this.run = null;
      this.notice = "";
      clearInterval(this.poll);
      return;
    }
    this.openActive(hit);
  }

  private loadActive(): void {
    if (!this.wfDefId) return;
    this.workflowService.activeRuns(this.wfDefId).subscribe((items) => {
      this.active = items || [];
      if (this.selectedId) {
        const hit = this.active.find((item) => Number(item.du_patient_id) === Number(this.selectedId));
        if (hit && Number(this.run?.wf_run_id) !== Number(hit.wf_run_id)) this.openActive(hit);
      }
    });
  }

  private openActive(hit: any): void {
    this.running = true;
    this.workflowService.runStatus(hit.wf_run_id).subscribe((res) => {
      this.running = false;
      if (!res) return;
      this.applyRun(res);
    });
  }

  answer(action: "escalate" | "wait" | "continue"): void {
    if (!this.run?.wf_run_id || this.running) return;
    this.running = true;
    this.workflowService.advanceRun(this.run.wf_run_id, action, this.frmDays).subscribe((res) => {
      this.running = false;
      if (!res) return;
      this.applyRun(res);
      this.loadActive();
    });
  }

  private applyRun(res: any): void {
    this.run = res;
    this.notice = res?.message || "";
    clearInterval(this.poll);
    if (res?.status === "WAITING" && res?.mode === "LIVE" && res?.phase === "waiting_delay") {
      this.poll = setInterval(() => this.pollStatus(), 15000);
    }
  }

  /** Reload patients, active runs, and the open run status. */
  refresh(): void {
    if (this.refreshing || this.running) return;
    this.refreshing = true;
    this.loadPatients();
    this.loadActive();
    if (!this.run?.wf_run_id) {
      this.refreshing = false;
      return;
    }
    this.workflowService.runStatus(this.run.wf_run_id).subscribe({
      next: (res) => {
        this.refreshing = false;
        if (res) this.applyRun(res);
      },
      error: () => {
        this.refreshing = false;
      },
    });
  }

  private pollStatus(): void {
    if (!this.run?.wf_run_id) return;
    this.workflowService.runStatus(this.run.wf_run_id).subscribe((res) => {
      if (res) this.applyRun(res);
      this.loadActive();
    });
  }
}
